import React from 'react';
import classnames from 'classnames/bind';
import { Column } from 'react-table';
import { ActionIcon, Badge, Menu } from '@mantine/core';

import {
  NotificationRule,
  getNotificationCategoryLabel,
} from 'types/notification_rule';
import { OffsetPaginated } from 'types/pagination';

import APIError from 'types/api_error';

import { useDeleteNotificationRules } from 'settings/hooks/useDeleteNotificationRules';
import { useUpdateNotificationRule } from 'settings/hooks/useUpdateNotificationRule';
import { useUserGroups } from 'settings/hooks/useUserGroups';
import { useModal } from 'hooks/useModal';

import { UseQueryResult } from '@tanstack/react-query';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { PaginatedTable } from 'components/Table/PaginatedTable';
import { Text } from 'components/Text/Text';

import settingsStyles from '../../Settings.module.scss';

const c = classnames.bind(settingsStyles);

type NotificationRulesTableProps = {
  queryResult: UseQueryResult<
    OffsetPaginated<NotificationRule> | undefined,
    APIError
  >;
  pageSize: number;
  page: number;
  onPageChange: (page: number) => void;
  onEdit: (rule: NotificationRule) => void;
};

export function NotificationRulesTable({
  queryResult,
  pageSize,
  page,
  onPageChange,
  onEdit,
}: NotificationRulesTableProps) {
  const { mutate: updateRule } = useUpdateNotificationRule();
  const { mutate: deleteRules } = useDeleteNotificationRules();
  const { data: userGroupsData } = useUserGroups({ params: { limit: 100 } });
  const { open } = useModal();

  const userGroupMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const group of userGroupsData?.data ?? []) {
      map.set(group.id, group.name);
    }
    return map;
  }, [userGroupsData]);

  const handleToggleEnabled = React.useCallback(
    (rule: NotificationRule) => {
      updateRule({ id: rule.id, enabled: !rule.enabled });
    },
    [updateRule]
  );

  const handleDelete = React.useCallback(
    (rule: NotificationRule) => {
      open('confirm-delete', {
        title: `Are you sure you want to delete "${rule.name}"?`,
        onConfirm: () => deleteRules({ notification_rule_ids: [rule.id] }),
      });
    },
    [deleteRules, open]
  );

  const columns = React.useMemo<Column<NotificationRule>[]>(
    () => [
      {
        Header: 'Name',
        Cell({ row: { original } }) {
          return <Heading level="4">{original.name}</Heading>;
        },
      },
      {
        Header: 'Categories',
        Cell({ row: { original } }) {
          return (
            <div className="flex flex-wrap gap-1">
              {original.notification_categories.map((cat) => (
                <Badge key={cat} size="sm" variant="light">
                  {getNotificationCategoryLabel(cat)}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        Header: 'Recipients',
        Cell({ row: { original } }) {
          const names = original.user_group_ids.map(
            (id) => userGroupMap.get(id) ?? id
          );
          return <Text>{names.join(', ') || '—'}</Text>;
        },
      },
      {
        Header: 'Filters',
        Cell({ row: { original } }) {
          return <Text>{countEntityFilterIds(original)}</Text>;
        },
      },
      {
        Header: 'Enabled',
        Cell({ row: { original } }) {
          return (
            <LightSwitch
              checked={original.enabled}
              onValueChange={() => handleToggleEnabled(original)}
            />
          );
        },
      },
      {
        id: 'actions',
        Cell({ row: { original } }) {
          return (
            <Menu position="bottom-end">
              <Menu.Target>
                <ActionIcon aria-label="More actions" variant="subtle">
                  <Icon name="more-vertical" unstyled />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onEdit(original)}>Edit</Menu.Item>
                <Menu.Item color="red" onClick={() => handleDelete(original)}>
                  Delete
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          );
        },
      },
    ],
    [handleDelete, handleToggleEnabled, onEdit, userGroupMap]
  );

  return (
    <PaginatedTable<NotificationRule>
      className={c('config-table')}
      id="notification-rules-table"
      label="Notification rules"
      queryResult={queryResult}
      columns={columns}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      emptyMessage={<EmptyView>No notification rules yet.</EmptyView>}
    />
  );
}

function countEntityFilterIds(rule: NotificationRule): number {
  const sets = [rule.entity_filters?.include, rule.entity_filters?.exclude];
  let total = 0;
  for (const set of sets) {
    if (!set) continue;
    total += set.gateway_ids?.length ?? 0;
    total += set.camera_ids?.length ?? 0;
    total += set.stream_ids?.length ?? 0;
    total += set.deployment_ids?.length ?? 0;
    total += set.pipeline_ids?.length ?? 0;
    total += set.tag_ids?.length ?? 0;
  }
  return total;
}
